import { keyframes } from '@emotion/core';
import styled from '@emotion/styled';
import { breakpointSizes, mediaQueries, mediaQueryBuilder, palette, remCalc, sizeMap } from '../../css';
import { StyledSvg } from '../../UI/Icon/IconSvg.css';

const rotateLoader = keyframes({
    from: { transform: 'rotate(0)' },
    to: { transform: 'rotate(720deg)' },
});

export const cacheStyle = {};

/**
 * Cache for avoiding unnecessary computing
 * @param {Object} props Key for caching (stringify of the object)
 *  and object transmitted to the fallback if no cache available
 * @param {Function} fallback Called if no cache avaiable for the props
 */
export const getCacheStyle = (props, fallback) => {
    const styleUID = JSON.stringify(props);
    let styles = cacheStyle[styleUID];

    if (!styles) {
        styles = fallback(props);
        cacheStyle[styleUID] = styles;
    }
    return styles;
};

/**
 * Computes style using props and distribute style for available breakpoints
 * @param {Function} configStyles Describes rules for CSS computing and treat with injected props
 * @param {Object} props Conditionalizes CSS computing (with the config styles)
 * @param {Function} renderStyle Returns the css rules using configuration
 */
export const computeStyles = (configStyles, props, renderStyle) => {
    const { breakpoints = ['base', ...Object.keys(breakpointSizes)], ...computedStyles } = configStyles(props);
    const availableBreakpoints = {};
    const styles = breakpoints.reduce((style, breakpoint) => {
        const breakpointStyle = {};
        Object.entries(computedStyles).forEach(([ruleName, ruleStyles]) => {
            if (typeof ruleStyles !== 'object') {
                if (breakpoint === 'base') {
                    breakpointStyle[ruleName] = ruleStyles;
                }
                return;
            }

            if (ruleStyles[breakpoint] !== undefined) {
                const affectedBreakpoints = Object.keys(ruleStyles)
                    .filter(rule => rule !== 'base')
                    .toString();
                if (!availableBreakpoints[affectedBreakpoints]) {
                    availableBreakpoints[affectedBreakpoints] = [];
                }
                if (availableBreakpoints[affectedBreakpoints].indexOf(ruleName) === -1) {
                    availableBreakpoints[affectedBreakpoints].push(ruleName);
                }
                breakpointStyle[ruleName] = ruleStyles[breakpoint];
            }
        });

        // Render only base rules
        if (breakpoint === 'base') {
            return {
                ...style,
                ...renderStyle(breakpointStyle),
            };
        }

        return style;
    }, {});

    // Render all non-base rules (all available breakpoints except base)
    const r = Object.entries(availableBreakpoints).reduce(
        (style, [breakpointPack, rules]) =>
            breakpointPack.split(',').reduce((s, bp, index, bps) => {
                const mediaQuery = mediaQueryBuilder(bp, bps[index + 1]);
                const breakpointStyle = rules.reduce(
                    (computedBreakpointStyles, rule) => ({
                        ...computedBreakpointStyles,
                        [rule]: computedStyles[rule][bp],
                    }),
                    {},
                );
                return {
                    ...s,
                    [mediaQuery]: renderStyle(breakpointStyle),
                };
            }, style),
        styles,
    );
    return r;
};

const teaserStyles = ({ kind, themeName, active, gutter = {} }) => {
    const styles = {
        breakpoints: ['base', ...Object.keys(breakpointSizes)],
        width: {},
        height: {},
        gutter: {},
        padding: {},
    };
    let ratio = 16 / 9; // default ratio (except for SingleLineSlider)

    switch (kind) {
        case 'LANDSCAPE':
        case 'LANDSCAPE_NOLABEL':
            styles.width = {
                base: 130,
                medium: 200,
                large: 250,
                xxlarge: 270,
            };
            styles.gutter = {
                base: gutter.mobile,
                medium: gutter.desktop,
            };
            break;
        case 'SQUARE':
            styles.width = {
                base: 100,
                medium: 130,
                large: themeName === 'concert' ? 200 : 130,
                xxlarge: themeName === 'concert' ? 200 : 155,
            };
            styles.gutter = {
                base: gutter.mobile,
                medium: gutter.desktop,
            };
            ratio = 1; // Square have a 1:1 ratio
            break;
        case 'VIDEO':
            styles.width = {
                base: 200,
                large: 250,
                xxlarge: 270,
            };
            styles.gutter = {
                base: gutter.mobile,
                medium: gutter.desktop,
            };
            break;
        case 'PORTRAIT':
            styles.width = {
                base: 130,
                large: 160,
                xlarge: 190,
            };
            styles.gutter = {
                base: gutter.mobile,
                medium: gutter.desktop,
            };
            ratio = 3 / 4;
            break;
        case 'PROGRAM_PLAYLIST':
            styles.width = {
                base: 160,
                medium: 280,
            };
            styles.padding = {
                base: remCalc(5),
                medium: remCalc(10),
            };
            styles.backgroundColor = active ? palette('grey', 'xxDark') : undefined;
            break;
        default:
            styles.width = {
                base: 160,
                medium: 240,
                xlarge: 325,
            };
            styles.gutter = {
                base: gutter.mobile,
                medium: gutter.desktop,
            };
            break;
    }
    if (styles.width) {
        Object.entries(styles.width).forEach(([breakpoint]) => {
            if (!styles.height[breakpoint]) {
                styles.height[breakpoint] = styles.width[breakpoint] / ratio;
            }
        });
    }

    return styles;
};

const concertButton = (nextOrPrev, theme) => {
    const isNext = nextOrPrev === 'next';
    const transform = (scale, withTranslate = true) =>
        `scale(${scale})${withTranslate ? ` translateX(${isNext ? '' : '-'}50%)` : ''}`;

    return {
        position: 'absolute',
        zIndex: 2,
        cursor: 'pointer',
        background: theme.singleLineSlider.buttonBackgroundColor,
        alignSelf: 'center',
        padding: remCalc(3),
        borderRadius: '100%',
        width: remCalc(25),
        height: remCalc(25),
        [isNext ? 'right' : 'left']: 0,
        marginTop: remCalc(-12.5),

        border: `1px solid ${palette('black')}`,
        transform: transform(1, !isNext),
        transition: 'transform .25s ease, border .5s ease',
        transformOrigin: `${isNext ? '100' : '0'}%`,

        ':focus, :hover': {
            border: `2px solid ${palette('black')}`,
            transform: transform(1.25, !isNext),
        },

        [StyledSvg]: {
            transform: `scale(1.3) rotate(${isNext ? '' : '-'}90deg) translateY(${remCalc(isNext ? 0 : -1)})`,
        },

        [mediaQueries.medium]: {
            width: remCalc(40),
            height: remCalc(40),
            padding: remCalc(10),
            marginTop: remCalc(-20),
            transform: transform(1),
            ':focus, :hover': {
                border: `2px solid ${palette('black')}`,
                transform: transform(1.25),
            },
        },
    };
};

const StyledWrapper = styled('div')(() => ({
    overflow: 'hidden',
    flex: 1,
}));

const StyledSlides = styled('div')(
    () => ({
        display: 'flex',
        transition: 'transform .75s ease',
    }),
    ({ touchDevice }) => {
        if (touchDevice) {
            return {
                overflowY: 'hidden',
                overflowX: 'scroll',
                WebkitOverflowScrolling: 'touch',
            };
        }
        return {};
    },
);

const StyledArrowPrev = styled('button')(
    ({ theme, themeName }) => {
        if (themeName === 'concert') {
            return concertButton('prev', theme);
        }

        return {
            backgroundColor: theme.singleLineSlider.buttonBackgroundColor,
            borderColor: 'transparent',
            borderStyle: 'solid',
            borderWidth: remCalc(2),
            zIndex: 2,
            cursor: 'pointer',

            [mediaQueries.medium]: {
                padding: remCalc(0, 10),
            },

            ':focus,:hover': {
                borderColor: theme.singleLineSlider.arrowFillColor,
                transition: 'border-color .3s linear',
            },
        };
    },
    ({ touchDevice }) =>
        touchDevice
            ? {
                  display: 'none',
              }
            : {},
);

const StyledArrowNext = styled('button')(
    ({ theme, themeName }) => {
        if (themeName === 'concert') {
            return concertButton('next', theme);
        }

        return {
            backgroundColor: theme.singleLineSlider.buttonBackgroundColor,
            borderColor: 'transparent',
            borderStyle: 'solid',
            borderWidth: remCalc(2),
            zIndex: 2,
            cursor: 'pointer',

            [mediaQueries.medium]: {
                padding: remCalc(0, 10),
            },

            ':focus,:hover': {
                borderColor: theme.singleLineSlider.arrowFillColor,
                transition: 'border-color .3s linear',
            },
        };
    },
    ({ loading, touchDevice }) => {
        if (touchDevice) {
            return {
                display: 'none',
            };
        }
        // ? test with 'true' because it's a dom element and the value should be stringify
        if (loading === 'true') {
            return {
                [StyledSvg]: {
                    animation: `${rotateLoader} 1s ease infinite alternate`,
                },
            };
        }
        return {};
    },
);

const StyledSlider = styled('div')(
    () => ({
        display: 'flex',
    }),
    ({ _kind: kind, themeName }) => {
        const styles = {};
        if (themeName === 'concert') {
            styles.position = 'relative';

            styles.p = {
                color: palette('grey', 'midDark'),
                fontSize: remCalc(sizeMap.xs * 1.05),
                padding: remCalc(4, 0),
            };

            const arrowPositions = getCacheStyle(
                {
                    kind,
                    themeName,
                },
                props =>
                    computeStyles(teaserStyles, props, ({ height }) => ({
                        top: remCalc(height / 2),
                    })),
            );

            styles[`${StyledArrowPrev},${StyledArrowNext}`] = {
                ...arrowPositions,
            };
        }

        return styles;
    },
);

const renderSlideStyle = ({ width, gutter, padding, backgroundColor }) => ({
    ...(width
        ? {
              flex: remCalc(0, 0, width),
              maxWidth: remCalc(width),
          }
        : {}),
    ...(gutter ? { marginRight: gutter } : {}),
    backgroundColor,
    padding,
});

const StyledSlide = styled('div')(({ _kind: kind, active, theme, themeName }) => {
    const gutter = {
        mobile: theme.slider.gutter.mobile,
        desktop: theme.slider.gutter.desktop,
    };
    const styles = getCacheStyle(
        {
            kind,
            themeName,
            active,
            gutter,
        },
        props => computeStyles(teaserStyles, props, renderSlideStyle),
    );

    if (kind === 'SQUARE') {
        return {
            ...styles,
            // TODO : use a better way for this (hack..)
            // Do not display sticker under xlarge (too large for square)
            [mediaQueries.xxlargeDown]: {
                '.live-sticker': {
                    display: 'none',
                },
            },
        };
    }

    return styles;
});

export { StyledArrowNext, StyledArrowPrev, StyledSlide, StyledSlider, StyledSlides, StyledWrapper };

